import React from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Minus, ArrowRight } from "lucide-react";
import { SectionReveal, StaggerWords } from "../components/Motion";
import { useT } from "../i18n/LanguageContext";

const Item = ({ q, a, i, open, onToggle }) => (
  <SectionReveal delay={i * 0.05} className="border-b border-[#4c535a]/20">
    <button
      data-testid={`faq-toggle-${i}`}
      onClick={onToggle}
      className="w-full flex items-start justify-between gap-6 py-6 md:py-8 text-left group"
      aria-expanded={open}
    >
      <span className="type-h2 text-[#4c535a] group-hover:text-[#4c535a] transition-colors duration-500">
        {q}
      </span>
      <span className="flex-none w-10 h-10 flex items-center justify-center border border-[#4c535a]/30 text-[#4c535a] transition-colors duration-500 group-hover:bg-[#4c535a] group-hover:text-[#EFECE6]">
        {open ? <Minus className="w-4 h-4" strokeWidth={1.4} /> : <Plus className="w-4 h-4" strokeWidth={1.4} />}
      </span>
    </button>
    <AnimatePresence initial={false}>
      {open && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          className="overflow-hidden"
        >
          <p className="pb-8 max-w-[62ch] type-body text-[#4c535a]/80">{a}</p>
        </motion.div>
      )}
    </AnimatePresence>
  </SectionReveal>
);

export default function FAQPage() {
  const { t } = useT();
  const [openIdx, setOpenIdx] = React.useState(0);
  return (
    <div className="pt-32 md:pt-40 pb-24 md:pb-32 bg-[#EFECE6]" data-testid="faq-page">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-12 gap-6 mb-16 md:mb-24">
          <p className="col-span-12 md:col-span-3 text-[11px] uppercase tracking-[0.28em] text-[#4c535a]">{t.faqPage.eyebrow}</p>
          <h1 className="col-span-12 md:col-span-9 type-display text-[#4c535a]">
            <StaggerWords text={t.faqPage.title_1} />
            <br />
            <StaggerWords text={t.faqPage.title_2} delay={0.15} className="italic font-light" />
          </h1>
        </div>

        <div className="grid grid-cols-12 gap-5 md:gap-8">
          <div className="hidden md:block md:col-span-3">
            <p className="text-[11px] uppercase tracking-[0.28em] text-[#4c535a] mb-3">{t.faqPage.aside_title}</p>
            <p className="text-[13px] text-[#4c535a]/70 max-w-[26ch] leading-[1.7]">{t.faqPage.aside_body}</p>
          </div>
          <div className="col-span-12 md:col-span-9 border-t border-[#4c535a]/20">
            {t.faqs.map((f, i) => (
              <Item key={f.q} q={f.q} a={f.a} i={i} open={openIdx === i} onToggle={() => setOpenIdx(openIdx === i ? -1 : i)} />
            ))}
          </div>
        </div>

        <div className="mt-24 md:mt-32 border-t border-[#4c535a]/25 pt-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <h3 className="type-h1 text-[#4c535a]">{t.faqPage.still}</h3>
          <Link to="/enquiry" data-testid="faq-cta-enquire" className="group inline-flex items-center gap-3 px-6 py-4 bg-[#4c535a] text-[#EFECE6] text-[11px] uppercase tracking-[0.24em] hover:bg-[#3d434a] transition-colors duration-500 flex-none">
            {t.faqPage.write}
            <ArrowRight className="w-4 h-4 transition-transform duration-500 group-hover:translate-x-1" strokeWidth={1.4} />
          </Link>
        </div>
      </div>
    </div>
  );
}
